/**
 * src/shell/auth/components/DecryptRevealTransition.tsx
 *
 * Hardware-accelerated Canvas zero-dependency cipher transition.
 * Rendered on session termination/logout for instant visual teardown (<16ms)
 * while background network and storage flushes occur non-blockingly.
 */

import React, { useEffect, useRef } from 'react';

interface DecryptRevealTransitionProps {
  durationMs?: number;
  onComplete?: () => void;
  message?: string;
}

const HEX_CHARS = '0123456789ABCDEF⚡🛡️MESH_TERMINATING_KEY_PURGE_';

export const DecryptRevealTransition: React.FC<DecryptRevealTransitionProps> = ({
  durationMs = 350,
  onComplete,
  message = 'TERMINATING SESSION & PURGING VAULT KEYS...',
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const fontSize = 14;
    const columns = Math.floor(canvas.width / fontSize);
    const drops = new Array(columns).fill(1);
    const startTime = performance.now();

    let animationFrameId: number;

    const render = (time: number) => {
      const elapsed = time - startTime;
      const progress = Math.min(elapsed / durationMs, 1);

      // Fade-out background trail
      ctx.fillStyle = 'rgba(3, 7, 18, 0.25)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Cyberpunk cipher stream
      ctx.fillStyle = '#0ea5e9';
      ctx.font = `${fontSize}px monospace`;

      for (let i = 0; i < drops.length; i++) {
        const text = HEX_CHARS[Math.floor(Math.random() * HEX_CHARS.length)];
        ctx.fillText(text, i * fontSize, drops[i] * fontSize);

        if (drops[i] * fontSize > canvas.height && Math.random() > 0.95) {
          drops[i] = 0;
        }
        drops[i]++;
      }

      // Center Cipher Scramble
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 16px monospace';
      ctx.textAlign = 'center';
      const scrambleChars = message
        .split('')
        .map((c) => (Math.random() < progress ? c : HEX_CHARS[Math.floor(Math.random() * 16)]))
        .join('');
      ctx.fillText(scrambleChars, canvas.width / 2, canvas.height / 2);

      if (progress < 1) {
        animationFrameId = requestAnimationFrame(render);
      } else if (onComplete) {
        onComplete();
      }
    };

    animationFrameId = requestAnimationFrame(render);

    return () => cancelAnimationFrame(animationFrameId);
  }, [durationMs, message, onComplete]);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: '#030712',
        pointerEvents: 'all',
      }}
    >
      <canvas ref={canvasRef} style={{ width: '100%', height: '100%', display: 'block' }} />
    </div>
  );
};
